import React, { useState } from 'react';
import { Link } from '../utils/router';
import logo from '../assets/img/logo.jpg';
import { motion, useScroll, useSpring, useMotionValueEvent, AnimatePresence } from "framer-motion";

const NavBar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false); // State for mobile menu
  
  const { scrollY, scrollYProgress } = useScroll();

  // Scroll Progress Bar Animation
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  // Detect scroll to toggle Glass Effect
  useMotionValueEvent(scrollY, "change", (latest) => {
    setIsScrolled(latest > 50);
  });

  // Link Data for cleaner mapping
  const navLinks = [
    { name: 'break.', path: '/break', gradient: 'from-pink-500 to-yellow-500' },
    { name: 'math.', path: '/math', gradient: 'from-indigo-500 via-purple-500 to-pink-500' },
    { name: 'projects.', path: '/projects', gradient: 'from-indigo-500 to-emerald-500' },
    { name: 'achieved.', path: '/achieved', gradient: 'from-cyan-500 to-blue-500' }
  ];

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          isScrolled 
            ? "bg-black/80 backdrop-blur-md border-b border-blue-600/50 py-2" 
            : "bg-transparent py-4 border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between">
            
            {/* --- LEFT: ORIGINAL LOGO --- */}
            <Link to="/" onClick={() => setIsOpen(false)}>
              <div className="flex group hover:cursor-pointer items-center">
                <div className="bg-slate-900/90 rounded-full flex items-center p-1 pr-3 border border-white/10">
                  <img src={logo} alt="mirkhoff_logo" className="w-8 h-8 rounded-full group-hover:animate-bounce" />
                  <span className="ml-2 font-medium group-hover:text-blue-600 align-middle text-white text-xs font-light transition duration-300">
                    @
                    <span className="underline decoration-double decoration-indigo-500 underline-offset-2">mirk</span>
                    h
                    <span className="underline decoration-double decoration-pink-500 underline-offset-2">o</span>
                    ff
                  </span>
                </div>

                <div className="hidden ml-2 md:flex items-center">
                  <span className="md:opacity-0 opacity-100 align-middle text-white text-xs group-hover:opacity-100 font-light transition-opacity duration-300 whitespace-nowrap"> 
                    &ndash; Security Researcher & Computer Engineer&reg;
                  </span>
                </div>
              </div>
            </Link>
            
            {/* --- DESKTOP NAV --- */}
            <div className="hidden md:flex items-center gap-2">
              {navLinks.map((link) => (
                <Link 
                  key={link.name}
                  to={link.path}
                  className={`
                    text-slate-400 text-sm font-medium px-3 py-2 rounded-md
                    transition-all duration-300 ease-out
                    hover:text-white hover:bg-gradient-to-r ${link.gradient}
                  `}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* --- MOBILE HAMBURGER BUTTON --- */}
            <div className="md:hidden">
              <button 
                onClick={() => setIsOpen(!isOpen)}
                className="text-white p-2 focus:outline-none"
              >
                {/* Switch between Hamburger and X icon */}
                {isOpen ? (
                   <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                   </svg>
                ) : (
                   <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                   </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* --- MOBILE MENU DROPDOWN --- */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-black/95 backdrop-blur-xl border-b border-blue-600 overflow-hidden"
            >
              <div className="flex flex-col px-4 py-4 space-y-2">
                {navLinks.map((link) => (
                  <Link 
                    key={link.name}
                    to={link.path}
                    onClick={() => setIsOpen(false)} // Close menu on click
                    className={`
                      block text-center text-slate-300 text-lg font-medium py-3 rounded-md
                      hover:text-white hover:bg-gradient-to-r ${link.gradient}
                    `}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* --- SCROLL PROGRESS BAR --- */}
        <motion.div 
          className="absolute bottom-0 left-0 h-[2px] bg-gradient-to-r from-blue-600 via-purple-500 to-red-500 origin-left" 
          style={{ scaleX }} 
        />
      </header>
    </>
  );
};

export default NavBar;
